import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TargetIcon, MailIcon, LayoutGridIcon, SearchIcon, UsersIcon, TrendingUpIcon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const strategies = [{
  id: 'advertising',
  icon: TargetIcon,
  title: 'Local advertising & targeted ads',
  description: 'Run hyper-targeted local advertising campaigns on Facebook Ads, Google Ads, and other platforms. Use first-party business data to build custom audiences that reach local business owners directly.',
  openmartHelps: 'Build custom audiences from 200M+ verified local business contacts for precision targeted advertising.'
}, {
  id: 'personalization',
  icon: MailIcon,
  title: 'Personalized outreach at scale',
  description: 'Personalized marketing drives higher engagement and customer loyalty. Use enriched contact data to craft personalized email campaigns and direct mail that resonates with each local business.',
  openmartHelps: 'Enrich every lead with 50+ data points for truly personalized marketing automation.'
}, {
  id: 'multichannel',
  icon: LayoutGridIcon,
  title: 'Multi-channel campaigns',
  description: 'Effective local marketing strategies span multiple channels — digital advertising, email sequences, direct mail, and community engagement. One source of verified data for omnichannel marketing.',
  openmartHelps: 'Export audience data to any channel — Meta Ads, Google Ads, CRM, email platforms, or direct mail.'
}, {
  id: 'seo',
  icon: SearchIcon,
  title: 'Local SEO & search optimization',
  description: 'Complement your local SEO strategy with outbound outreach. Combine local search data with verified contact information for a complete digital marketing strategy that accelerates growth.',
  openmartHelps: 'Identify businesses in any location and category to fuel both inbound and outbound local marketing.'
}, {
  id: 'community',
  icon: UsersIcon,
  title: 'Community engagement & partnerships',
  description: 'Build lasting community marketing relationships by reaching local business owners directly. Use verified owner data for partnerships, co-marketing campaigns, and local business networks.',
  openmartHelps: 'Find decision-makers at local businesses for partnership outreach and community marketing initiatives.'
}, {
  id: 'roi',
  icon: TrendingUpIcon,
  title: 'Marketing ROI & performance',
  description: 'Maximize marketing ROI by targeting only verified, high-intent local business contacts. Data-driven local business advertising delivers measurable return on ad spend and accelerates growth.',
  openmartHelps: '95%+ email deliverability and 90%+ ad match rates mean higher conversion and better marketing ROI.'
}];
export function MarketingStrategies() {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, {
      passive: true
    });
    checkScroll();
    return () => el.removeEventListener('scroll', checkScroll);
  }, []);
  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('div')?.offsetWidth || 340;
    el.scrollBy({
      left: direction === 'left' ? -cardWidth - 24 : cardWidth + 24,
      behavior: 'smooth'
    });
  };
  return <section className="py-24 bg-slate-50 border-t border-slate-100" data-id="element-5105">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5106">
        <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-5107">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-5108">
            Data-driven{' '}
            <span className="text-blue-600" data-id="element-5109">local marketing strategies</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-5110">
            Power your marketing playbook with verified local business data
            across every channel.
          </p>
        </div>

        {/* Scroll controls */}
        <div className="flex items-center justify-end gap-2 mb-3" data-id="element-5111">
          <button onClick={() => scroll('left')} disabled={!canScrollLeft} className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-5112">
            <ChevronLeftIcon className="w-5 h-5" data-id="element-5113" />
          </button>
          <button onClick={() => scroll('right')} disabled={!canScrollRight} className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-5114">
            <ChevronRightIcon className="w-5 h-5" data-id="element-5115" />
          </button>
        </div>

        {/* Scrollable cards */}
        <div ref={scrollRef} className="flex gap-6 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }} data-id="element-5116">
          {strategies.map(strategy => {
          const Icon = strategy.icon;
          return <div key={strategy.id} className="flex-shrink-0 w-[340px] md:w-[380px] bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col snap-start" data-id="element-5117">
                <div className="p-7 flex-1 flex flex-col" data-id="element-5118">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-4" data-id="element-5119">
                    <Icon className="w-5 h-5 text-blue-600" data-id="element-5120" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-5121">
                    {strategy.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed flex-1" data-id="element-5122">
                    {strategy.description}
                  </p>
                </div>
                <div className="px-7 pb-7 mt-auto" data-id="element-5123">
                  <div className="border-l-2 border-blue-500 pl-3 py-0.5" data-id="element-5124">
                    <div className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-1" data-id="element-5125">
                      How Openmart helps
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed" data-id="element-5126">
                      {strategy.openmartHelps}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}